import { CreditCardCommandService, buildCreditCardCommandIdempotencyKey } from "@/lib/creditCardCommandService";
import { CreditCardPurchaseCommandService } from "@/lib/creditCardPurchaseCommandService";
import { CreditCardPaymentIntentService } from "@/lib/creditCardPaymentIntentService";

export type CreditCardAmountMode = "total" | "installment";
export type CreditCardInvoiceStatus = "open" | "closed" | "paid" | "overdue";
export type CreditCardInvoiceFinancialState =
  | "open"
  | "closed"
  | "overdue"
  | "paid"
  | "paid_with_pending_difference"
  | "paid_with_credit";
export type CreditCardTransactionType = "purchase" | "refund" | "adjustment";

export type CreditCardEngineCard = {
  id: string;
  closing_day: number | string;
  due_day: number | string;
};

export type CreditCardEnginePurchase = {
  id: string;
  user_id: string;
  card_id: string;
  invoice_id: string | null;
  description?: string | null;
  category?: string | null;
  sub_category?: string | null;
  notes?: string | null;
  amount: number | string;
  purchase_date?: string | null;
  installments?: number | string | null;
  current_installment?: number | string | null;
  invoice_month: string;
  status: "pending" | "paid" | "overdue" | "cancelled" | null;
  paid_at: string | null;
  paid_from_account_id: string | null;
  installment_group_id?: string | null;
  refund_reason?: string | null;
};

export type CreditCardEngineInvoice = {
  id: string;
  user_id: string;
  card_id: string;
  invoice_month: string;
  closing_date: string;
  due_date: string;
  total_amount: number | string;
  status: CreditCardInvoiceStatus;
  paid_at: string | null;
  paid_from_account_id: string | null;
  paid_amount?: number | string | null;
  paid_transaction_id?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type CreditCardEngineAccount = {
  id: string;
  current_balance: number | string;
};

export type PayCreditCardInvoiceOptions = {
  userId: string;
  invoice: CreditCardEngineInvoice;
  cardName: string;
  account?: CreditCardEngineAccount | null;
  transactionDate?: string;
};

export type ReopenCreditCardInvoiceOptions = {
  userId: string;
  invoice: CreditCardEngineInvoice;
  card: CreditCardEngineCard & { name?: string };
};

export type CreditCardPurchaseEditScope = "single" | "future" | "all";

export type UpdateCreditCardPurchaseChanges = {
  targetCardId?: string;
  description?: string | null;
  category?: string | null;
  subCategory?: string | null;
  notes?: string | null;
  refundReason?: string | null;
  amount?: number;
  purchaseDate?: string;
  invoiceMonth?: string | null;
};

export type UpdateCreditCardPurchaseOptions = {
  userId: string;
  card: CreditCardEngineCard;
  purchase: CreditCardEnginePurchase;
  relatedPurchases?: CreditCardEnginePurchase[];
  scope?: CreditCardPurchaseEditScope;
  changes: UpdateCreditCardPurchaseChanges;
  existingInvoices?: CreditCardEngineInvoice[];
};

export type DeleteCreditCardPurchaseOptions = {
  userId: string;
  card: CreditCardEngineCard;
  purchase: CreditCardEnginePurchase;
  relatedPurchases?: CreditCardEnginePurchase[];
  scope?: CreditCardPurchaseEditScope;
  existingInvoices?: CreditCardEngineInvoice[];
};

function normalizeNullableText(value: string | null | undefined) {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

function normalizeOptionalText(value: string | null | undefined) {
  return value?.trim() ?? "";
}

function moneyChanged(current: number | string | null | undefined, next?: number) {
  if (next === undefined) return false;
  return Math.round(Number(current ?? 0) * 100) !== Math.round(next * 100);
}

function textChanged(current: string | null | undefined, next: string | null | undefined) {
  if (next === undefined) return false;
  return normalizeOptionalText(current) !== normalizeOptionalText(next);
}

function nullableTextChanged(
  current: string | null | undefined,
  next: string | null | undefined
) {
  if (next === undefined) return false;
  return normalizeNullableText(current) !== normalizeNullableText(next);
}

export function isInvoicePaidStatus(status: CreditCardInvoiceStatus | string | null | undefined) {
  return status === "paid";
}

export function isInvoiceOpenStatus(status: CreditCardInvoiceStatus | string | null | undefined) {
  return status === "open" || status === "closed" || status === "overdue";
}

export function isInvoicePayableStatus(status: CreditCardInvoiceStatus | string | null | undefined) {
  return isInvoiceOpenStatus(status);
}

export function isInvoiceSettledStatus(invoice: Pick<CreditCardEngineInvoice, "status"> | null | undefined) {
  return isInvoicePaidStatus(invoice?.status);
}

export function isInvoiceReopenableStatus(
  invoice: Pick<CreditCardEngineInvoice, "status" | "paid_at"> | null | undefined
) {
  return isInvoicePaidStatus(invoice?.status) && Boolean(invoice?.paid_at);
}

export function getInvoicePaidAmount(
  invoice: Pick<CreditCardEngineInvoice, "status" | "total_amount" | "paid_amount"> | null | undefined
) {
  if (!isInvoicePaidStatus(invoice?.status)) return 0;
  return Number(invoice?.paid_amount ?? invoice?.total_amount ?? 0);
}

export function getInvoiceCorrectionBalance(
  invoice: Pick<CreditCardEngineInvoice, "status" | "total_amount" | "paid_amount"> | null | undefined
) {
  if (!isInvoicePaidStatus(invoice?.status)) return 0;
  return Number(invoice?.total_amount ?? 0) - getInvoicePaidAmount(invoice);
}

export function getInvoiceFinancialState(
  invoice: Pick<CreditCardEngineInvoice, "status" | "total_amount" | "paid_amount"> | null | undefined
): CreditCardInvoiceFinancialState {
  if (!invoice) return "open";
  if (!isInvoicePaidStatus(invoice.status)) {
    return invoice.status === "closed" || invoice.status === "overdue"
      ? invoice.status
      : "open";
  }

  const correctionBalance = getInvoiceCorrectionBalance(invoice);
  if (correctionBalance > 0.005) return "paid_with_pending_difference";
  if (correctionBalance < -0.005) return "paid_with_credit";
  return "paid";
}

function resolveEditTargets({
  purchase,
  relatedPurchases = [],
  scope,
}: {
  purchase: CreditCardEnginePurchase;
  relatedPurchases?: CreditCardEnginePurchase[];
  scope: CreditCardPurchaseEditScope;
}) {
  if (scope === "single" || Number(purchase.installments ?? 1) <= 1) return [purchase];

  const currentInstallment = Number(purchase.current_installment ?? 1);
  const base = relatedPurchases.length > 0 ? relatedPurchases : [purchase];

  return base
    .filter((item) =>
      scope === "future"
        ? Number(item.current_installment ?? 1) >= currentInstallment
        : true
    )
    .sort(
      (a, b) =>
        Number(a.current_installment ?? 0) - Number(b.current_installment ?? 0)
    );
}

export function shiftMonth(value: string, amount: number) {
  const [year, month] = value.split("-").map(Number);
  const date = new Date(year, month - 1 + amount);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function clampDay(year: number, monthIndex: number, day: number) {
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return Math.min(Math.max(day, 1), lastDay);
}

function toDateOnly(year: number, monthIndex: number, day: number) {
  return new Date(year, monthIndex, day).toISOString().slice(0, 10);
}

export function calculateInvoiceMonth(
  purchaseDate: string,
  closingDay: number,
  dueDay?: number
) {
  const [year, month, day] = purchaseDate.split("-").map(Number);
  const purchaseMonth = `${year}-${String(month).padStart(2, "0")}`;

  if (dueDay !== undefined && closingDay < dueDay) {
    return day <= closingDay ? purchaseMonth : shiftMonth(purchaseMonth, 1);
  }

  return day <= closingDay ? shiftMonth(purchaseMonth, 1) : shiftMonth(purchaseMonth, 2);
}

export const getCreditCardInvoiceMonth = calculateInvoiceMonth;

export function calculateInvoiceDates(
  invoiceMonth: string,
  closingDay: number,
  dueDay: number
) {
  const [invoiceYear, invoiceMonthNumber] = invoiceMonth.split("-").map(Number);
  const dueMonthIndex = invoiceMonthNumber - 1;
  const closingMonthOffset = closingDay < dueDay ? 0 : -1;
  const closingBase = new Date(invoiceYear, dueMonthIndex + closingMonthOffset, 1);
  const previousClosingBase = new Date(invoiceYear, dueMonthIndex + closingMonthOffset - 1, 1);
  const closingDayValue = clampDay(
    closingBase.getFullYear(),
    closingBase.getMonth(),
    closingDay
  );
  const previousClosingDayValue = clampDay(
    previousClosingBase.getFullYear(),
    previousClosingBase.getMonth(),
    closingDay
  );
  const dueDayValue = clampDay(invoiceYear, dueMonthIndex, dueDay);
  const periodEnd = new Date(
    closingBase.getFullYear(),
    closingBase.getMonth(),
    closingDayValue - 1
  );

  return {
    periodStart: toDateOnly(
      previousClosingBase.getFullYear(),
      previousClosingBase.getMonth(),
      previousClosingDayValue
    ),
    periodEnd: periodEnd.toISOString().slice(0, 10),
    closingDate: toDateOnly(closingBase.getFullYear(), closingBase.getMonth(), closingDayValue),
    dueDate: toDateOnly(invoiceYear, dueMonthIndex, dueDayValue),
  };
}

export const getCreditCardInvoiceDates = calculateInvoiceDates;

export function calculateInvoiceStatus(
  invoiceMonth: string,
  closingDay: number,
  dueDay: number,
  referenceDate?: string | Date | null
): CreditCardInvoiceStatus {
  const { closingDate, dueDate } = calculateInvoiceDates(invoiceMonth, closingDay, dueDay);
  const today = referenceDate
    ? typeof referenceDate === "string"
      ? referenceDate.slice(0, 10)
      : referenceDate.toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);

  if (today > dueDate) return "overdue";
  if (today >= closingDate) return "closed";
  return "open";
}

export const getCreditCardInvoiceStatusValue = calculateInvoiceStatus;

export function getResolvedInvoiceStatus(
  invoice: Pick<CreditCardEngineInvoice, "status" | "invoice_month"> | null | undefined,
  card: Pick<CreditCardEngineCard, "closing_day" | "due_day"> | null | undefined,
  referenceDate?: string | Date | null
): CreditCardInvoiceStatus {
  if (!invoice) return "open";
  if (isInvoicePaidStatus(invoice.status)) return "paid";
  if (!card) return invoice.status ?? "open";

  return calculateInvoiceStatus(
    invoice.invoice_month,
    Number(card.closing_day),
    Number(card.due_day),
    referenceDate
  );
}

export function getCreditCardInvoiceStatusLabel(status: CreditCardInvoiceStatus) {
  const labels: Record<CreditCardInvoiceStatus, string> = {
    open: "Aberta",
    closed: "Fechada",
    paid: "Paga",
    overdue: "Vencida",
  };
  return labels[status];
}

export function resolveCreditCardInvoice({
  purchaseDate,
  closingDay,
  dueDay,
  manualInvoiceMonth,
}: {
  purchaseDate: string;
  closingDay: number;
  dueDay?: number;
  manualInvoiceMonth?: string | null;
}) {
  const calculatedInvoiceMonth = calculateInvoiceMonth(purchaseDate, closingDay, dueDay);
  return {
    invoiceMonth: manualInvoiceMonth || calculatedInvoiceMonth,
    calculatedInvoiceMonth,
    isManual: Boolean(manualInvoiceMonth),
  };
}

export function getInstallmentAmounts(
  rawAmount: number,
  installments: number,
  amountMode: CreditCardAmountMode
) {
  if (installments <= 1) return [Number(rawAmount.toFixed(2))];
  if (amountMode === "installment") {
    return Array.from({ length: installments }, () => Number(rawAmount.toFixed(2)));
  }

  const totalCents = Math.round(rawAmount * 100);
  const baseCents = Math.floor(totalCents / installments);
  const remainder = totalCents - baseCents * installments;

  return Array.from({ length: installments }, (_, index) =>
    Number(((baseCents + (index < remainder ? 1 : 0)) / 100).toFixed(2))
  );
}

export function buildCreditCardInstallmentSchedule({
  amount,
  count,
  amountMode,
  firstInvoiceMonth,
  startInstallment = 1,
}: {
  amount: number;
  count: number;
  amountMode: CreditCardAmountMode;
  firstInvoiceMonth: string;
  startInstallment?: number;
}) {
  return getInstallmentAmounts(amount, count, amountMode)
    .slice(startInstallment - 1)
    .map((installmentAmount, index) => ({
      amount: installmentAmount,
      currentInstallment: startInstallment + index,
      invoiceMonth: shiftMonth(firstInvoiceMonth, index),
    }));
}

export async function syncCreditCardInvoices({
  card, userId, extraInvoiceMonths = [],
}: {
  card: CreditCardEngineCard;
  userId: string;
  existingInvoices?: CreditCardEngineInvoice[];
  extraInvoiceMonths?: string[];
}) {
  return (await CreditCardPurchaseCommandService.mutate({
    userId, cardId: card.id, operation: "sync", extraMonths: extraInvoiceMonths,
  })).invoices;
}

export async function updateCreditCardPurchase({
  userId, card, purchase, scope = "single", changes,
}: UpdateCreditCardPurchaseOptions) {
  return CreditCardPurchaseCommandService.mutate({
    userId, cardId: card.id, purchaseId: purchase.id, scope, operation: "update", changes,
  });
}

export async function moveCreditCardPurchaseToInvoice({
  userId, purchaseId, fromInvoiceMonth, targetInvoiceMonth, card,
}: {
  userId: string;
  purchaseId: string;
  fromInvoiceMonth: string;
  targetInvoiceMonth: string;
  card: CreditCardEngineCard;
  existingInvoices?: CreditCardEngineInvoice[];
}) {
  if (targetInvoiceMonth === fromInvoiceMonth) throw new Error("target_invoice_must_be_different");
  return (await CreditCardPurchaseCommandService.mutate({
    userId, cardId: card.id, purchaseId, operation: "update",
    changes: { invoiceMonth: targetInvoiceMonth },
  })).invoices;
}

export async function deleteCreditCardPurchase({
  userId, card, purchase, scope = "single",
}: DeleteCreditCardPurchaseOptions) {
  return CreditCardPurchaseCommandService.mutate({
    userId, cardId: card.id, purchaseId: purchase.id, scope, operation: "delete",
  });
}

export async function payCreditCardInvoice({
  userId, invoice, account, transactionDate = new Date().toISOString().slice(0, 10),
}: PayCreditCardInvoiceOptions) {
  const amount = Number(invoice.total_amount);
  const result = await CreditCardPaymentIntentService.register(userId, {
    invoiceId: invoice.id, amount, accountId: account?.id ?? null,
    paymentDate: transactionDate,
  });
  return {
    invoice: result.invoice, paidAt: result.payment.effective_at,
    amount: Number(result.payment.amount), paidFromAccountId: result.payment.account_id,
    paidTransactionId: result.transaction_id,
    nextAccountBalance: result.account === null ? null : Number(result.account.current_balance),
  };
}

export async function reopenCreditCardInvoice({ invoice }: ReopenCreditCardInvoiceOptions) {
  const result = await CreditCardCommandService.reopenInvoice({
    invoiceId: invoice.id, reopenedAt: new Date().toISOString(),
    idempotencyKey: buildCreditCardCommandIdempotencyKey("reopen", [
      invoice.id, invoice.paid_at, invoice.paid_amount ?? invoice.total_amount, invoice.paid_transaction_id,
    ]),
  });
  const account = result.accounts[0] ?? null;
  return {
    invoice: result.invoice, nextStatus: result.invoice.status,
    restoredAccountId: account?.id ?? null,
    restoredAccountBalance: account === null ? null : Number(account.current_balance),
  };
}
